import {
  ProductHistoryItem,
  ProvisioningStep,
} from '../../models/ProductHistory';
import { ProductUsageCount } from '../../models/ProductUsageCount';
import { ProjectLink } from '../../models/ProjectLink';
import { ProvisioningOrder } from '../../models/ProvisioningOrder';
import { UserLicenseStatus } from '../../models/UserLicenseStatus';
import { DataResult } from '../../utils/DataResult';
import {
  CancelUserLicenseOptions,
  OrderUserLicenseOptions,
  UpdateCustomFormDataOptions,
  UpdateUserLicenseOptions,
} from '../api';
import { BaseApiService, toRequestError } from './http';
import {
  isProductUsageCount,
  isProjectLink,
  isProvisioningOrder,
  sanitizeProjectLink,
} from './shared';

function isProductHistoryItem(value: unknown): value is ProductHistoryItem {
  const item = value as ProductHistoryItem | null;

  return (
    typeof item === 'object' &&
    item !== null &&
    typeof item.operation === 'string' &&
    item.operation.length > 0
  );
}

function toProvisioningSteps(value: unknown): ProvisioningStep[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (step): step is ProvisioningStep =>
        typeof step === 'object' && step !== null,
    )
    .map(step => ({
      ...step,
      steps:
        step.steps === undefined ? undefined : toProvisioningSteps(step.steps),
    }));
}

function toProductHistoryItem(item: ProductHistoryItem): ProductHistoryItem {
  const { IsRunning } = item as ProductHistoryItem & { IsRunning?: unknown };

  return {
    ...item,
    isRunning: item.isRunning ?? IsRunning === true,
    steps: toProvisioningSteps(item.steps),
  };
}

function isUserLicenseStatus(value: unknown): value is UserLicenseStatus {
  const status = value as UserLicenseStatus | null;

  return (
    typeof status === 'object' &&
    status !== null &&
    typeof status.licenseInstanceId === 'string' &&
    status.licenseInstanceId.length > 0 &&
    Array.isArray(status.orders)
  );
}

/**
 * Handles user licenses: ordering, cancellation, history, status and the
 * custom form data / budget actions tied to a license.
 */
export class LicenseService extends BaseApiService {
  async getUserLicenseUsageCounts(): Promise<ProductUsageCount[]> {
    return this.fetchArray('/user/licenses/counts', isProductUsageCount);
  }

  async getUserLicenseProvisioningOrders(): Promise<ProvisioningOrder[]> {
    return this.fetchArray('/user/licenses/orders', isProvisioningOrder);
  }

  async getUserLicenseLinks(): Promise<ProjectLink[]> {
    const links = await this.fetchArray('/user/licenses/links', isProjectLink);

    return links.map(sanitizeProjectLink);
  }

  async getUserLicenseHistory(
    licenseId: string,
  ): Promise<ProductHistoryItem[]> {
    if (!licenseId) {
      return [];
    }

    const history = await this.fetchArray(
      `/provisioning/current_user/${encodeURIComponent(licenseId)}`,
      isProductHistoryItem,
    );

    return history.map(toProductHistoryItem);
  }

  async getUserLicenseStatus(): Promise<UserLicenseStatus[]> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/user/licenses/pendingorders`,
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }

    const body: unknown = await response.json();
    const statuses = Array.isArray(body)
      ? body
      : (body as DataResult<unknown[]> | null)?.data;

    if (!Array.isArray(statuses)) {
      return [];
    }

    return statuses.filter(isUserLicenseStatus);
  }

  async orderUserLicense({
    catalogProductRefId,
    catalogVariantRefId,
    ou,
    costCenter,
    internalOrderNumber,
    customFormData,
    validateOnly,
  }: OrderUserLicenseOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const query = new URLSearchParams();
    query.set('originType', 'Odp');

    if (validateOnly) {
      query.set('validateOnly', 'true');
    }

    const search = query.toString();
    const suffix = search ? `?${search}` : '';

    await this.post(`${baseUrl}/user/licenses${suffix}`, {
      catalogProductRefId,
      catalogVariantRefId,
      ou: ou?.trim(),
      costCenter: costCenter?.trim(),
      internalOrderNumber: internalOrderNumber?.trim(),
      customFormData:
        customFormData && customFormData.length > 0
          ? customFormData
          : undefined,
    });
  }

  async cancelUserLicense({
    licenseId,
    force,
  }: CancelUserLicenseOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const query = new URLSearchParams();
    query.set('originType', 'Odp');

    if (force !== undefined) {
      query.set('force', String(force));
    }

    const search = query.toString();
    const suffix = search ? `?${search}` : '';

    const response = await this.fetchApi.fetch(
      `${baseUrl}/user/licenses/${encodeURIComponent(licenseId)}${suffix}`,
      { method: 'DELETE' },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }
  }

  async undoCancelUserLicense(licenseId: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const query = new URLSearchParams();
    query.set('originType', 'Odp');

    const search = query.toString();
    const suffix = search ? `?${search}` : '';

    await this.post(
      `${baseUrl}/user/licenses/${encodeURIComponent(licenseId)}${suffix}`,
    );
  }

  async updateUserLicense({
    licenseId,
    catalogProductRefId,
    catalogVariantRefId,
    ou,
    costCenter,
    internalOrderNumber,
  }: UpdateUserLicenseOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const query = new URLSearchParams();
    query.set('originType', 'Odp');

    const search = query.toString();
    const suffix = search ? `?${search}` : '';

    await this.put(
      `${baseUrl}/user/licenses/${encodeURIComponent(licenseId)}${suffix}`,
      {
        licenseInstanceId: licenseId,
        catalogProductRefId,
        catalogVariantRefId,
        ou: ou?.trim(),
        costCenter: costCenter?.trim(),
        internalOrderNumber: internalOrderNumber?.trim(),
      },
    );
  }

  async cancelUserLicenseReview(
    userId: string,
    licenseId: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const query = new URLSearchParams();
    query.set('originType', 'Odp');

    const search = query.toString();
    const suffix = search ? `?${search}` : '';

    const response = await this.fetchApi.fetch(
      `${baseUrl}/user/licenses/${encodeURIComponent(
        userId,
      )}/cancellicensereview${suffix}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([licenseId]),
      },
    );

    if (!response.ok) {
      throw await toRequestError(response);
    }
  }

  async keepUserLicenseReview(
    userId: string,
    licenseId: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    await this.post(
      `${baseUrl}/user/licenses/${encodeURIComponent(
        userId,
      )}/keeplicensereview`,
      [licenseId],
    );
  }

  async updateLicenseCustomFormData({
    customFormId,
    data,
    instanceId,
    justification,
  }: UpdateCustomFormDataOptions): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const query = new URLSearchParams();
    query.set('licenseInstanceId', instanceId);
    query.set('originType', 'Odp');

    const search = query.toString();
    const suffix = search ? `?${search}` : '';

    await this.put(`${baseUrl}/user/licenses/formdata${suffix}`, {
      id: customFormId,
      data,
      justification: justification ?? null,
    });
  }

  async resetTeamMemberBudget(
    userId: string,
    licenseInstanceId: string,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    await this.put(
      `${baseUrl}/supervisor/${encodeURIComponent(
        licenseInstanceId,
      )}/users/${encodeURIComponent(userId)}/resetbudget`,
    );
  }
}
